import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Ticker from "../components/Ticker";
import { ArticlesAPI, normalizeArticle, cropUnsplash } from "../lib/api";
import SmartImage from "../components/SmartImage";
import { useAuth } from "../lib/auth";
import TierRibbon from "../components/TierRibbon";

/**
 * LibraryPage — subscriber-only article list.
 *
 * Renders a filtered list of articles at the specified tier:
 *   /library/premium       → readTier=premium (visible to premium + professional)
 *   /library/professional  → readTier=professional (professional only)
 *
 * Access matrix:
 *   Anon / Free              → wall + subscribe CTA
 *   Premium subscriber       → sees Premium library only. Professional page shows wall.
 *   Professional subscriber  → sees Professional library AND (below it) all Premium articles.
 *
 * The Professional page intentionally lists Premium articles under a
 * separator ("Also included from Premium") because a Professional
 * subscription is a superset of Premium.
 */
const LibraryPage = ({ tier }) => {
  const { user } = useAuth();
  const [primary, setPrimary] = useState([]);
  const [premiumExtra, setPremiumExtra] = useState([]);  // only used on the Professional page
  const [loading, setLoading] = useState(true);

  const entitlement = (user?.subscription?.tier || user?.tier || "free").toLowerCase();
  const isProfessional = entitlement === "professional";
  const isPremium = entitlement === "premium" || isProfessional;
  const entitled = tier === "premium" ? isPremium : isProfessional;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ArticlesAPI.list({ limit: 200, readTier: tier })
      .then((data) => { if (!cancelled) setPrimary(data.map(normalizeArticle)); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    // Professional page: also fetch Premium articles (they're included).
    if (tier === "professional") {
      ArticlesAPI.list({ limit: 200, readTier: "premium" })
        .then((data) => { if (!cancelled) setPremiumExtra(data.map(normalizeArticle)); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [tier]);

  const label = tier === "premium" ? "Premium" : "Professional";
  const priceLabel = tier === "premium" ? "$12 / month" : "$50 / month";

  return (
    <div>
      <Ticker />
      <Header />
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="border-b-2 border-[#0B1F33] pb-4 mb-8">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#0891B2] font-semibold">
            Vexent Wire · Library
          </div>
          <h1 className="mt-2 font-serif-display text-[36px] md:text-[48px] font-bold text-[#0B1F33] leading-tight">
            {label} Research
          </h1>
          <p className="mt-2 text-[14px] text-neutral-600 max-w-[720px]">
            {tier === "premium"
              ? "Full daily research library, weekly market reports, downloadable PDFs, and ad-free reading."
              : "Institutional research, strategic intelligence, quarterly outlook reports, and every Premium article included."}
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16 text-neutral-500 gap-2" data-testid="library-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading library…
          </div>
        )}

        {!loading && !entitled && (
          <div
            data-testid="library-paywall"
            className="border-2 border-[#B8E9F1] bg-white p-8 md:p-12 max-w-[720px]"
          >
            <Lock className="w-8 h-8 text-[#0891B2] mb-4" />
            <div className="font-condensed uppercase tracking-[0.3em] text-[11px] font-bold text-[#0891B2]">
              Subscriber Access
            </div>
            <h2 className="mt-2 font-serif-display text-[28px] font-bold text-[#0B1F33] leading-tight">
              This library is for {label} subscribers
            </h2>
            <p className="mt-3 text-[14px] text-neutral-700 leading-relaxed">
              {primary.length > 0
                ? `${primary.length} ${label.toLowerCase()} article${primary.length === 1 ? "" : "s"} available inside.`
                : "New research is added weekly."}{" "}
              Subscribe for {priceLabel} to unlock the entire {label} archive.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/subscribe"
                data-testid="library-subscribe-cta"
                className="inline-flex items-center gap-2 bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] font-condensed uppercase tracking-[0.2em] text-[12px] font-bold px-5 py-3 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                {entitlement === "premium" && tier === "professional"
                  ? "Upgrade to Professional"
                  : `Start ${label}`}
              </Link>
              <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">
                {tier === "premium" ? "30-day free trial · cancel anytime" : "Cancel anytime"}
              </span>
            </div>
          </div>
        )}

        {!loading && entitled && primary.length === 0 && (
          <div className="border border-neutral-300 bg-white p-8 text-center text-neutral-600">
            No {label.toLowerCase()} articles have been published yet. Check back soon.
          </div>
        )}

        {!loading && entitled && primary.length > 0 && (
          <>
            <ArticleGrid items={primary} sectionLabel={`${label} · ${primary.length}`} />
            {tier === "professional" && premiumExtra.length > 0 && (
              <>
                <div className="mt-16 mb-6 border-b border-neutral-300 pb-3">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#0891B2] font-semibold">
                    Also Included
                  </div>
                  <h2 className="mt-1 font-serif-display text-[26px] font-bold text-[#0B1F33]">
                    Every Premium Article
                  </h2>
                  <p className="mt-1 text-[13px] text-neutral-600">
                    Your Professional subscription includes complete access to the Premium library.
                  </p>
                </div>
                <ArticleGrid items={premiumExtra} sectionLabel={`Premium · ${premiumExtra.length}`} />
              </>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

const ArticleGrid = ({ items, sectionLabel }) => (
  <div data-testid={`library-grid-${sectionLabel?.split(" ")[0].toLowerCase()}`}>
    <div className="mb-3 text-[10px] tracking-[0.28em] uppercase text-neutral-500 font-semibold">
      {sectionLabel}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((a) => (
        <Link
          key={a.id}
          to={`/article/${a.slug}`}
          data-testid={`library-card-${a.slug}`}
          className="group block bg-white border border-neutral-200 hover:border-[#B8E9F1] transition-colors"
        >
          {a.image && (
            <div className="aspect-[16/10] overflow-hidden bg-neutral-100">
              <SmartImage
                src={cropUnsplash(a.image, { w: 1200, h: 750 })}
                alt={a.title}
                sizes="(min-width: 1024px) 33vw, 50vw"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          )}
          <div className="p-4">
            <h3 className="font-serif-display text-[18px] font-bold leading-[1.2] text-[#0B1F33] group-hover:text-[#0891B2] transition-colors line-clamp-3">
              {a.title}
            </h3>
            {a.dek && (
              <p className="mt-2 text-[12.5px] text-neutral-600 leading-snug line-clamp-2">{a.dek}</p>
            )}
            <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
              <span>By {a.author || "Max Fischer"}</span>
              <TierRibbon tier={a.readTier} size="xxs" className="ml-auto" />
            </div>
          </div>
        </Link>
      ))}
    </div>
  </div>
);

export default LibraryPage;
